import React, { useEffect, useRef, useState } from 'react';
import { Loader2, AlertCircle, Info } from 'lucide-react';

// Shown at the top of every Shopify page so an empty page is never mistaken for missing data:
// first import still running, import failed, store has no orders yet, or nothing in the chosen range.
const POLL_MS = 10000;

const formatDay = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

export default function ShopifySyncStatus({ rangeEmpty = false, onShowAll, onReady }) {
  const [status, setStatus] = useState(null);
  const wasImporting = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const load = async () => {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;
      try {
        const base = window.location.hostname === 'localhost' ? '' : 'https://api.glowytics.com';
        const res = await fetch(`${base}/api/shopify/sync-status`, { credentials: 'include', headers: { 'X-Brand-ID': brandId } });
        const js = await res.json().catch(() => null);
        if (cancelled || !res.ok || !js?.ok) return;
        setStatus(js);
        if (js.importing) {
          wasImporting.current = true;
          timer = setTimeout(load, POLL_MS);
        } else if (wasImporting.current) {
          wasImporting.current = false;
          if (onReady) onReady(); else window.location.reload();
        }
      } catch (e) {
        // Status is advisory; the page still renders its own data.
      }
    };
    load();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!status) return null;
  const { importing, failed, connected, orders } = status;
  const lastOrder = formatDay(orders?.lastOrderDate);

  let tone = 'info';
  let title = null;
  let body = null;
  let action = null;

  if (importing) {
    title = 'Importing your Shopify orders';
    body = 'The first import usually takes about a minute. This page refreshes automatically when it is done.';
  } else if (failed) {
    tone = 'error';
    title = "The Shopify import didn't finish";
    body = 'Open the Glowytics app in your Shopify admin to reconnect, or email hello@glowytics.com.';
  } else if (!connected && !orders?.count) {
    title = 'Shopify is not connected';
    body = 'Install the Glowytics app from the Shopify App Store and connect it to this brand to see your store data.';
  } else if (!orders?.count) {
    title = 'No orders yet';
    body = 'Your store is connected but has no orders yet. New orders are imported every day.';
  } else if (rangeEmpty) {
    title = 'No orders in the selected date range';
    body = lastOrder ? `Your most recent order is from ${lastOrder}.` : null;
    if (onShowAll) action = { label: 'Show all time', onClick: onShowAll };
  } else {
    return null;
  }

  const styles = tone === 'error'
    ? 'bg-red-50 border-red-200 text-red-800'
    : 'bg-blue-50 border-blue-200 text-blue-900';
  const Icon = importing ? Loader2 : tone === 'error' ? AlertCircle : Info;

  return (
    <div className={`rounded-2xl border p-4 flex items-start gap-3 ${styles}`} role="status">
      <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${importing ? 'animate-spin' : ''}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold">{title}</p>
        {body && <p className="text-sm mt-0.5 opacity-90">{body}</p>}
      </div>
      {action && (
        <button
          className="px-3 py-1.5 text-sm font-medium rounded-lg bg-white border border-current/20 hover:bg-gray-50 shrink-0"
          onClick={action.onClick}
        >
          {action.label}
        </button>
      )}
    </div>
  );
}
